import { FC, useCallback, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import ButtonGroup from '../../common/components/ButtonGroup';
import {
  ConfigureConnectionInput,
  Connection,
} from '../types/connection-types';
import {
  addConnectionTooltip,
  removeConnectionTooltip,
} from '../utils/button-tooltip-utils';
import { useSnapshot } from 'valtio';
import ScanStore from '../../new-scan/store/scan-store';

const AddRemoveButtonSwitcher: FC<
  React.PropsWithChildren<AddRemoveButtonSwitcherProps>
> = props => {
  const {
    onClose,
    connection,
    onAddConnection,
    onRemoveConnection,
    isReadOnlyRole,
    isLoadingAdd,
    isLoadingRemove,
    isSelfConnection,
  } = props;

  const configureConnectionInput = useMemo(() => {
    return { connectionId: connection.id } as ConfigureConnectionInput;
  }, [connection]);

  const { isDemoEnabled } = useSnapshot(ScanStore);

  const handleOnAddConnection = useCallback(() => {
    onAddConnection(configureConnectionInput);
  }, [onAddConnection, configureConnectionInput]);

  const handleOnRemoveConnection = useCallback(() => {
    onRemoveConnection(configureConnectionInput);
  }, [onRemoveConnection, configureConnectionInput]);

  const isConnectionEnabled = connection.enabled;

  const isRemoveDisabled = isSelfConnection || isReadOnlyRole || isDemoEnabled;

  const addConnectionTooltipTitle = addConnectionTooltip(
    isReadOnlyRole || isDemoEnabled,
  );
  const removeConnectionTooltipTitle = removeConnectionTooltip(
    isReadOnlyRole || isDemoEnabled,
    isSelfConnection,
  );

  return (
    <Box display='flex' flexDirection='column' gap={3} mt={1}>
      <Stack
        gap={2}
        width='100%'
        justifyContent='flex-end'
        direction='row'
        alignItems='center'>
        {isConnectionEnabled ? (
          <ButtonGroup
            onCancel={onClose}
            onClick={handleOnRemoveConnection}
            isLoading={isLoadingRemove}
            buttonCancelLabel={'Cancel'}
            buttonCancelTooltip={'Cancel this operation'}
            buttonActionLabel={'Revoke'}
            buttonActionTooltip={removeConnectionTooltipTitle}
            buttonActionColor={'error'}
            isActionDisabled={isRemoveDisabled}
          />
        ) : (
          <ButtonGroup
            onCancel={onClose}
            onClick={handleOnAddConnection}
            isLoading={isLoadingAdd}
            buttonCancelLabel={'Cancel'}
            buttonCancelTooltip={'Cancel this operation'}
            buttonActionLabel={'Allow'}
            buttonActionTooltip={addConnectionTooltipTitle}
            buttonActionColor={'success'}
            isActionDisabled={isReadOnlyRole || isDemoEnabled}
          />
        )}
      </Stack>
    </Box>
  );
};

export interface AddRemoveButtonSwitcherProps {
  connection: Connection;
  onClose: () => void;
  onAddConnection: (configureConnectionInput: ConfigureConnectionInput) => void;
  onRemoveConnection: (
    configureConnectionInput: ConfigureConnectionInput,
  ) => void;
  isReadOnlyRole: boolean;
  isLoadingAdd: boolean;
  isLoadingRemove: boolean;
  isSelfConnection: boolean;
}

export default AddRemoveButtonSwitcher;
